import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiHeart,
  FiPackage,
  FiSearch,
  FiUsers,
} from "react-icons/fi";
import siteConfig from "../config/siteConfig";
import "../css/about.scss";

const values = [
  {
    icon: FiSearch,
    number: "01",
    title: "Taste comes first",
    text: "Every cheese, preserve, and pantry staple earns its place through thoughtful tasting—not trend chasing.",
  },
  {
    icon: FiUsers,
    number: "02",
    title: "Know the maker",
    text: "We look for the people, places, and patient techniques that turn good ingredients into memorable food.",
  },
  {
    icon: FiPackage,
    number: "03",
    title: "Pack with purpose",
    text: "From a weeknight wedge to a generous gift, every order should arrive ready to open, share, and enjoy.",
  },
  {
    icon: FiHeart,
    number: "04",
    title: "Keep it generous",
    text: "Hospitality guides the details: useful advice, honest portions, and a warm welcome for every appetite.",
  },
];

const chapters = [
  {
    label: "Chapter one",
    title: "The first market table",
    text: "The idea began with a simple question: what if a neighborhood counter could make discovering exceptional food feel relaxed and personal?",
  },
  {
    label: "Chapter two",
    title: "A pantry takes shape",
    text: "Cheese was joined by cured meats, bright preserves, crisp crackers, and the small essentials that turn tasting into a table.",
  },
  {
    label: "Chapter three",
    title: "Gifts worth gathering around",
    text: "Our favorite pairings became generous boxes designed to travel well and make the person opening them feel truly considered.",
  },
  {
    label: "Today",
    title: "Market spirit, delivered",
    text: "We bring the energy of an old-city food hall online: curious, abundant, a little lively, and always led by flavor.",
  },
];

const people = [
  {
    role: "The cheesemonger",
    quote: "Starts with the bite, then finds the story worth telling.",
    accent: "RIND / RIPENING / REGION",
  },
  {
    role: "The pantry curator",
    quote: "Builds the bridge between a beautiful ingredient and an easy meal.",
    accent: "PAIRING / PROVENANCE / PURPOSE",
  },
  {
    role: "The gift packer",
    quote: "Treats every box like the first course of a celebration.",
    accent: "CARE / CRAFT / ARRIVAL",
  },
];

function About() {
  return (
    <main className="about-page">
      <section className="about-page__hero">
        <div className="about-page__hero-copy">
          <p className="about-page__eyebrow">From the market, with care</p>
          <h1>Food worth gathering around.</h1>
          <p className="about-page__lede">
            {siteConfig.description} We bring together expressive cheese,
            old-world pantry staples, and generous gifts with the warmth of a
            neighborhood counter.
          </p>
          <div className="about-page__actions">
            <Link className="about-page__button about-page__button--light" to="/shop">
              Explore the pantry <FiArrowRight aria-hidden="true" />
            </Link>
            <a className="about-page__text-link" href="#our-story">
              Read our story
            </a>
          </div>
        </div>

        <div className="about-page__hero-art" aria-label="A market-inspired tasting card">
          <div className="about-page__market-stamp" aria-hidden="true">
            <span>{siteConfig.brandName}</span>
            <small>GATHER · TASTE · SHARE</small>
          </div>
          <div className="about-page__tasting-card about-page__tasting-card--back">
            <span>THE OLD CITY PANTRY</span>
          </div>
          <div className="about-page__tasting-card about-page__tasting-card--front">
            <p>TODAY&apos;S TASTING NOTES</p>
            <strong>Bold, bright &amp; made to share</strong>
            <ul>
              <li>Aged &amp; savory</li>
              <li>Sweet &amp; unexpected</li>
              <li>Always generous</li>
            </ul>
            <span className="about-page__ticket-number">NO. 193</span>
          </div>
        </div>
      </section>

      <section className="about-page__intro" id="our-story">
        <p className="about-page__kicker">Our point of view</p>
        <div className="about-page__intro-grid">
          <h2>A great food shop should feel like a conversation.</h2>
          <div>
            <p>
              {siteConfig.brandName} is inspired by the old-city market: counters
              full of color, generous tastes passed across the aisle, and people
              who can tell you exactly why something is delicious.
            </p>
            <p>
              We translate that feeling into an online pantry built for discovery.
              You will find familiar comforts beside a few delicious surprises,
              all selected to be useful, giftable, and genuinely good at the table.
            </p>
          </div>
        </div>
      </section>

      <section className="about-page__values" aria-labelledby="values-title">
        <div className="about-page__section-heading">
          <p className="about-page__kicker">What guides us</p>
          <h2 id="values-title">The house rules</h2>
        </div>
        <div className="about-page__value-grid">
          {values.map(({ icon: Icon, number, title, text }) => (
            <article className="about-page__value-card" key={title}>
              <div className="about-page__value-topline">
                <Icon aria-hidden="true" />
                <span>{number}</span>
              </div>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-page__chapters" aria-labelledby="chapters-title">
        <div className="about-page__chapters-heading">
          <p className="about-page__kicker">How the pantry grew</p>
          <h2 id="chapters-title">Four chapters, one appetite.</h2>
          <p>
            Our story is not about being the biggest shelf. It is about building a
            better one, one thoughtful addition at a time.
          </p>
        </div>
        <ol className="about-page__timeline">
          {chapters.map((chapter, index) => (
            <li key={chapter.title}>
              <span className="about-page__timeline-index">{index + 1}</span>
              <div>
                <p>{chapter.label}</p>
                <h3>{chapter.title}</h3>
                <span>{chapter.text}</span>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="about-page__people" aria-labelledby="people-title">
        <div className="about-page__section-heading about-page__section-heading--split">
          <div>
            <p className="about-page__kicker">Hands behind the hamper</p>
            <h2 id="people-title">Curiosity is our craft.</h2>
          </div>
          <p>
            The best selection still needs a human point of view. These are the
            roles that turn ingredients into guidance, pairings, and gifts.
          </p>
        </div>
        <div className="about-page__people-grid">
          {people.map((person, index) => (
            <article className="about-page__person" key={person.role}>
              <span className="about-page__person-number">0{index + 1}</span>
              <div className="about-page__person-mark" aria-hidden="true">
                {person.role.charAt(4)}
              </div>
              <p>{person.accent}</p>
              <h3>{person.role}</h3>
              <blockquote>“{person.quote}”</blockquote>
            </article>
          ))}
        </div>
      </section>

      <section className="about-page__cta">
        <div>
          <p className="about-page__kicker">Pull up a chair</p>
          <h2>Find your next favorite thing.</h2>
          <p>
            Start with a classic, add something curious, and make the board your
            own.
          </p>
        </div>
        <div className="about-page__cta-actions">
          <Link className="about-page__button about-page__button--dark" to="/shop">
            Shop all food <FiArrowRight aria-hidden="true" />
          </Link>
          <Link className="about-page__text-link about-page__text-link--dark" to="/contact">
            Talk to us
          </Link>
        </div>
      </section>
    </main>
  );
}

export default About;
